import { motion } from "framer-motion";
import { imageUrl, money, API } from "@/lib/api";
import { Plus, Share2, MapPin } from "lucide-react";
import { toast } from "sonner";

async function shareItem(kind, item) {
  const url = `${API}/share/${kind}/${item.id}`;
  const data = { title: `${item.name} — Exotic Reefing Inc`, text: `Check out ${item.name} at Exotic Reefing Inc`, url };
  try {
    if (navigator.share) { await navigator.share(data); }
    else { await navigator.clipboard.writeText(url); toast.success("Share link copied — paste it anywhere!"); }
  } catch (e) { /* user cancelled share */ }
}

export function ProductCard({ product, onAdd, index = 0 }) {
  const isEquip = product.category === "equipment";
  const catLabel = isEquip ? (product.condition === "used" ? "Used" : "New") : product.category === "coral" ? "Coral" : "Fish";
  const subLabel = isEquip ? (product.equipment_type || "Equipment") : (product.species || "\u00A0");
  return (
    <motion.div
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
      className="card-glow group relative rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02]"
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <img src={imageUrl(product.image)} alt={product.name} loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-void via-void/20 to-transparent" />
        <span data-testid={`product-badge-${product.id}`} className={`absolute top-4 left-4 mono-label px-3 py-1 rounded-full border ${isEquip && product.condition === "used" ? "bg-void/70 border-toxic/40 text-toxic" : "bg-void/70 border-white/10"}`}>
          {catLabel}
        </span>
        {isEquip && product.pickup_only && (
          <span data-testid={`pickup-only-${product.id}`} className="absolute top-14 left-4 mono-label text-[0.55rem] text-cyan border border-cyan/40 bg-void/70 px-2.5 py-1 rounded-full flex items-center gap-1">
            <MapPin size={11} /> Pickup only
          </span>
        )}
        {product.stock <= 2 && (
          <span className="absolute top-4 right-4 mono-label text-toxic border border-toxic/40 bg-void/70 px-3 py-1 rounded-full">
            {product.stock === 0 ? "Sold" : `${product.stock} left`}
          </span>
        )}
        <button data-testid={`share-product-${product.id}`} onClick={() => shareItem("product", product)}
          title="Share" aria-label="Share this item"
          className="absolute bottom-4 right-4 w-9 h-9 rounded-full bg-void/70 border border-white/15 flex items-center justify-center text-white/70 hover:text-cyan hover:border-cyan/50 transition-colors opacity-0 group-hover:opacity-100">
          <Share2 size={15} />
        </button>
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between mb-1">
          <p className="text-white/40 font-mono text-[0.7rem] italic">{subLabel}</p>
          {product.origin && <span className="mono-label text-[0.55rem] text-cyan/80">◈ {product.origin}</span>}
        </div>
        <h3 className="font-display text-2xl leading-tight mb-2">{product.name}</h3>
        <p className="text-white/50 text-sm line-clamp-2 mb-5 min-h-[2.5rem]">{product.description}</p>
        <div className="flex items-center justify-between">
          <span className="font-mono text-cyan text-lg">{money(product.price)}</span>
          <button data-testid={`add-to-cart-${product.id}`} onClick={() => onAdd(product)}
            disabled={product.stock === 0}
            className="btn-pill !py-2.5 !px-4 flex items-center gap-2 disabled:opacity-40">
            <Plus size={15} /> Add
          </button>
        </div>
      </div>
    </motion.div>
  );
}
